import { Control, Controller, RegisterOptions } from "react-hook-form";
import { useState } from "react";
import { NoxusFormLabel } from "./NoxusFormLabel";
import { ConfirmationDialog } from "./ConfirmationDialog";
import { Button } from "@/components/shadcn/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/shadcn/dropdown-menu";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { ANALYTICS_EVENTS, analytics_track } from "@/utils/mixpanel";

export type MenuOption = {
  value: string;
  label: string;
  description?: string;
  icon?: React.ReactNode;
};

interface IMenuSelectProps {
  name?: string;
  value?:
    | string
    | {
        label: string;
        value: string;
        description?: string;
        icon?: React.ReactNode;
      };
  placeholder?: string;
  options: MenuOption[];
  label?: string;
  tooltip?: string;
  helperText?: string;
  isDisabled?: boolean;
  isHorizontal?: boolean;
  isOptional?: boolean;
  showOptionalLabel?: boolean;
  isClearable?: boolean;
  onChange?: (value: string | null) => void;
  onBlur?: (value: any) => void;
  buttonLabel?: React.ReactNode;
  buttonVariant?:
    | "default"
    | "destructive"
    | "outline"
    | "secondary"
    | "ghost"
    | "link";
  buttonSize?: "default" | "sm" | "lg" | "icon";
  buttonIcon?: React.ReactNode;
  className?: string;
}

export const MenuSelect = ({
  name,
  value,
  options,
  placeholder,
  label,
  tooltip,
  helperText,
  isDisabled = false,
  isHorizontal = false,
  isOptional,
  showOptionalLabel,
  onChange,
  buttonLabel,
  buttonVariant = "outline",
  buttonSize = "default",
  buttonIcon,
  className,
}: IMenuSelectProps) => {
  const currentValue =
    typeof value === "string" || typeof value === "number"
      ? (value as string)
      : (value as any)?.value;

  const selectedOption = options.find((o) => o.value === currentValue);
  const computedSelectedLabel =
    selectedOption?.label || placeholder || "Select";
  const displayedLabel = buttonLabel ?? computedSelectedLabel;
  const triggerIcon = selectedOption?.icon ?? buttonIcon;

  const content = (
    <>
      {options.map((opt) => (
        <DropdownMenuItem
          key={opt.value}
          onClick={(e) => {
            e.stopPropagation();
            if (onChange) {
              onChange(opt.value);
            }
          }}
          disabled={isDisabled}
          className={cn(
            "flex flex-col items-start gap-0",
            currentValue === opt.value && "bg-accent",
            opt.description && "py-2"
          )}
        >
          <div className="flex items-center gap-2">
            {opt.icon}{" "}
            <span className={cn("truncate", opt.description && "font-medium")}>
              {opt.label}
            </span>
          </div>
          {opt?.description && (
            <span className="text-xs text-muted-foreground">
              {opt.description}
            </span>
          )}
        </DropdownMenuItem>
      ))}
    </>
  );

  if (isHorizontal) {
    return (
      <div
        className={cn(
          "ConfigMenuSelect flex justify-between gap-6 relative",
          helperText ? "items-start" : "items-center",
          className
        )}
      >
        {label && (
          <div className="flex-1">
            <NoxusFormLabel
              label={label}
              tooltip={tooltip}
              name={name}
              showOptionalLabel={showOptionalLabel}
              isRequired={!isOptional}
              noMargin
            />
            {helperText != undefined && (
              <p className="text-muted-foreground text-xs whitespace-pre-line mt-1.5">
                {helperText}
              </p>
            )}
          </div>
        )}

        <div className="shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant={buttonVariant}
                size={buttonSize}
                disabled={isDisabled}
                className="gap-1.5 max-w-48 justify-between"
              >
                <span className="flex items-center gap-1.5">
                  {triggerIcon}
                  <span
                    className={cn(
                      "truncate",
                      !buttonLabel && !currentValue && "text-muted-foreground"
                    )}
                  >
                    {displayedLabel}
                  </span>
                </span>
                <ChevronDown size="0.75rem" className="shrink-0" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-[--radix-dropdown-menu-trigger-width] min-w-[16rem]"
              align="end"
              side="bottom"
            >
              {content}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("ConfigMenuSelect w-full", className)}>
      {label && (
        <NoxusFormLabel
          label={label}
          tooltip={tooltip}
          name={name}
          showOptionalLabel={showOptionalLabel}
          isRequired={!isOptional}
        />
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant={buttonVariant}
            size={buttonSize}
            disabled={isDisabled}
            className="w-full justify-between"
          >
            <span className="flex items-center gap-2">
              {triggerIcon}
              <span
                className={cn(
                  !buttonLabel && !currentValue && "text-muted-foreground"
                )}
              >
                {displayedLabel}
              </span>
            </span>
            <ChevronDown size="0.75rem" className="shrink-0" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          side="bottom"
          className="w-[--radix-dropdown-menu-trigger-width] min-w-[16rem]"
        >
          {content}
        </DropdownMenuContent>
      </DropdownMenu>

      {helperText != undefined && (
        <p
          className="text-muted-foreground text-xs whitespace-pre-line"
          style={{ marginTop: "0.25rem" }}
        >
          {helperText}
        </p>
      )}
    </div>
  );
};

interface IMenuSelectFieldProps {
  name: string;
  placeholder?: string;
  label?: string;
  helperText?: string;
  tooltip?: string;
  options: MenuOption[];
  rules?: RegisterOptions;
  control: Control<any>;
  isDisabled?: boolean;
  isHorizontal?: boolean;
  defaultValue?: string;
  isOptional?: boolean;
  showOptionalLabel?: boolean;
  isClearable?: boolean;
  needsConfirmation?: boolean;
  confirmationMessage?: string;
  onChange?: (value: string) => void;
  buttonLabel?: React.ReactNode;
  buttonVariant?:
    | "default"
    | "destructive"
    | "outline"
    | "secondary"
    | "ghost"
    | "link";
  buttonSize?: "default" | "sm" | "lg" | "icon";
  buttonIcon?: React.ReactNode;
  className?: string;
  mixpanelEvent?: string;
}

export const MenuSelectField = (props: IMenuSelectFieldProps) => {
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingValue, setPendingValue] = useState<string | null>(null);
  const [pendingOnChange, setPendingOnChange] = useState<(() => void) | null>(
    null
  );

  const handleConfirm = () => {
    if (pendingOnChange) pendingOnChange();
    setShowConfirmation(false);
    setPendingValue(null);
    setPendingOnChange(null);
  };

  const handleCancel = () => {
    setShowConfirmation(false);
    setPendingValue(null);
    setPendingOnChange(null);
  };

  return (
    <>
      <Controller
        control={props.control}
        name={props.name}
        defaultValue={props.defaultValue}
        rules={props.rules}
        render={({ field: { onChange, value, onBlur } }) => {
          const handleChange = (selected: string | null) => {
            const optionValue = selected || "";

            if (props.needsConfirmation) {
              setPendingValue(optionValue);
              setPendingOnChange(() => () => {
                onChange(optionValue);
                if (props.mixpanelEvent) {
                  analytics_track(props.mixpanelEvent as ANALYTICS_EVENTS, {
                    value: optionValue,
                  });
                }
                if (props.onChange) props.onChange(optionValue);
              });
              setShowConfirmation(true);
            } else {
              onChange(optionValue);
              if (props.mixpanelEvent) {
                analytics_track(props.mixpanelEvent as ANALYTICS_EVENTS, {
                  value: optionValue,
                });
              }
              if (props.onChange) props.onChange(optionValue);
            }
          };

          return (
            <MenuSelect
              name={props.name}
              value={value}
              label={props.label}
              options={props.options}
              tooltip={props.tooltip}
              helperText={props.helperText}
              placeholder={props.placeholder}
              isDisabled={props.isDisabled}
              isHorizontal={props.isHorizontal}
              onChange={handleChange}
              onBlur={onBlur}
              isOptional={props.isOptional}
              showOptionalLabel={props.showOptionalLabel}
              isClearable={props.isClearable}
              buttonLabel={props.buttonLabel}
              buttonVariant={props.buttonVariant}
              buttonSize={props.buttonSize}
              buttonIcon={props.buttonIcon}
              className={props.className}
            />
          );
        }}
      />

      {props.needsConfirmation && (
        <ConfirmationDialog
          open={showConfirmation}
          onOpenChange={setShowConfirmation}
          title="Confirm change"
          message={
            props.confirmationMessage ||
            "Are you sure you want to make this change?"
          }
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </>
  );
};
